import React, { ReactNode } from 'react'

import { CopyButton } from '@/components/copy-button'
import { cn } from '@/lib/utils'

import { Badge } from './badge'

export type FeedbackLayout = 'default' | 'compact'
export type FeedbackVariant =
  | 'error'
  | 'info'
  | 'warning'
  | 'success'
  | 'default'

interface FeedbackContextValue {
  layout: FeedbackLayout
  variant: FeedbackVariant
}

const FeedbackContext = React.createContext<FeedbackContextValue>({
  layout: 'default',
  variant: 'default',
})

interface FeedbackProps {
  children: ReactNode
  layout?: FeedbackLayout
  variant?: FeedbackVariant
  className?: string
}

const variantStyles: Record<FeedbackVariant, { border: string; bg: string }> = {
  error: { border: 'border-red-200', bg: 'bg-red-50' },
  info: { border: 'border-blue-200', bg: 'bg-blue-50' },
  warning: { border: 'border-yellow-200', bg: 'bg-yellow-50' },
  success: { border: 'border-green-200', bg: 'bg-green-50' },
  default: { border: 'border-border', bg: 'bg-background' },
}

export function Feedback({
  children,
  layout = 'default',
  variant = 'default',
  className,
}: FeedbackProps) {
  const styles = variantStyles[variant]

  return (
    <FeedbackContext.Provider value={{ layout, variant }}>
      <div
        className={cn(
          layout === 'default' && 'flex flex-col items-center justify-center',
          layout === 'compact' &&
            'flex w-full flex-wrap items-center justify-between gap-4 rounded-xl p-4',
          layout === 'compact' && styles.border,
          layout === 'compact' && styles.bg,
          className,
        )}
      >
        {children}
      </div>
    </FeedbackContext.Provider>
  )
}

interface FeedbackImageProps {
  src: string
  alt: string
  width?: number
  height?: number
  className?: string
}

export function FeedbackImage({
  src,
  alt,
  width = 190,
  height = 190,
  className,
}: FeedbackImageProps) {
  const { layout } = React.useContext(FeedbackContext)

  if (layout === 'compact') {
    return null
  }

  return (
    <div className={cn('mb-2', className)}>
      <img src={src} alt={alt} width={width} height={height} />
    </div>
  )
}

interface FeedbackContentProps {
  children: ReactNode
  className?: string
}

export function FeedbackContent({ children, className }: FeedbackContentProps) {
  const { layout } = React.useContext(FeedbackContext)

  if (layout === 'compact') {
    return <div className={cn('flex-1', className)}>{children}</div>
  }

  return (
    <div className={cn('flex flex-col items-center text-center', className)}>
      {children}
    </div>
  )
}

interface FeedbackTitleProps {
  children: ReactNode
  className?: string
}

const variantTitleColors: Record<FeedbackVariant, string> = {
  error: 'text-red-600 [&>svg]:text-red-600',
  info: 'text-blue-600 [&>svg]:text-blue-600',
  warning: 'text-yellow-600 [&>svg]:text-yellow-600',
  success: 'text-green-600 [&>svg]:text-green-600',
  default: 'text-foreground [&>svg]:text-foreground',
}

export function FeedbackTitle({ children, className }: FeedbackTitleProps) {
  const { layout, variant } = React.useContext(FeedbackContext)

  return (
    <h3
      className={cn(
        'flex items-center gap-2 font-medium',
        layout === 'default' && 'text-xl text-foreground',
        layout === 'compact' && 'text-base leading-6 [&>svg]:size-4',
        layout === 'compact' && variantTitleColors[variant],
        className,
      )}
    >
      {children}
    </h3>
  )
}

interface FeedbackDescriptionProps {
  children: ReactNode
  className?: string
}

const variantDescriptionColors: Record<FeedbackVariant, string> = {
  error: 'text-red-600',
  info: 'text-blue-600',
  warning: 'text-yellow-600',
  success: 'text-green-600',
  default: 'text-muted-foreground',
}

export function FeedbackDescription({
  children,
  className,
}: FeedbackDescriptionProps) {
  const { layout, variant } = React.useContext(FeedbackContext)

  return (
    <p
      className={cn(
        'max-w-lg text-sm font-normal',
        layout === 'default' && 'mt-2 text-muted-foreground',
        layout === 'compact' && cn('pl-6', variantDescriptionColors[variant]),
        className,
      )}
    >
      {children}
    </p>
  )
}

interface FeedbackActionProps {
  children: ReactNode
  className?: string
}

export function FeedbackAction({ children, className }: FeedbackActionProps) {
  const { layout } = React.useContext(FeedbackContext)

  if (layout === 'compact') {
    return <div className={cn('flex-shrink-0', className)}>{children}</div>
  }

  return <div className={cn('mt-4', className)}>{children}</div>
}

interface FeedbackEventIdProps {
  eventId: string
  className?: string
}

export function FeedbackEventId({ eventId, className }: FeedbackEventIdProps) {
  return (
    <Badge
      variant="outline"
      className={cn(
        'group relative mb-1 mt-3 flex w-max items-center gap-1 rounded-md px-2 py-1 font-mono text-xs font-normal',
        className,
      )}
    >
      <span className="text-muted-foreground">EVENT ID: </span>
      <span className="font-semibold text-foreground">{eventId}</span>
      <CopyButton value={eventId} />
    </Badge>
  )
}
